import { IntegrationReportRules } from '@app/model/integration-settings/Integration';
// BI domain (BI_TOOL, v3.0.0): the reports that read the imported reservations
// (rms_reservation_state / rms_reservation_nights_state). Values are sums of nights:
// - a sold Room Night = a night of the newest version of a booked room that is not cancelled and counts_for_room_nights;
// - Total = rates_price of the nights: the room and the meals of the rate, with VAT; extras are in no revenue;
// - Room only (roomRevenue) = daily_nights_total of the same nights: the night price without its meals (ROO2-235);
// - each also without VAT (Net): daily_nights_vat_net_total and daily_vat_net_total. A sum is null when one of its nights
//   has no value of it (no room only value yet, or a room type with no VAT rate): never a partial sum, never 0 (ROO2-236).

// The BI data of one hotel of the BI scope: an enabled hotel of the company with the BI domain (BI_TOOL), which the user
// may read with BI access. The names and places of the hotels come from the account of the user
// (findMyCompanyHotelsLite), not from the BI query.
export type BiHotel = {
	companyHotelId: number,
	// The label of the provider of the Reservations integration ("5stelle"). null: no successful reservations import yet.
	reservationsSource: string | null,
	// The end of the newest successful reservations import of the hotel, ISO date-time in the company time zone with its
	// offset (2026-10-14T16:46:21+03:00). null: no import yet.
	importedAt: string | null,
	// The first and the last stay date with a sold night, YYYY-MM-DD. null: no sold night.
	firstStay: string | null,
	lastStay: string | null,
	// ROO2-302 (Q1 C): the hotel has held rooms of group blocks from the as-of day on. The reports that do not count them yet
	// (Segmentation, Channels, Rate plans, Room Types) say so in their captions.
	heldBlocks?: boolean,
};

// One property of the account with its BI data: the account hotel joined with its BiHotel.
export type BiProperty = BiHotel & {
	name: string,
	// The city of the hotel.
	area: string,
};

// The answer of findBiScope.
export type BiScopeResponse = {
	companyId: number,
	companyName: string,
	// Today for the reports: the date in the company time zone, YYYY-MM-DD.
	asOf: string,
	// The enabled hotels of the company with the BI domain (BI_TOOL), also the hotels that the user cannot see: the groups apply to them.
	companyProperties: number,
	// The hotels of the BI scope of the user.
	hotels: BiHotel[],
};

export type BiScope = {
	companyId: number,
	companyName: string,
	// Today for the reports: the date in the company time zone, YYYY-MM-DD.
	asOf: string,
	// For all properties of the account: the OLDEST importedAt of the properties with reservations (each property is at
	// least that fresh), the first firstStay and the last lastStay.
	importedAt: string | null,
	firstStay: string | null,
	lastStay: string | null,
	// The enabled hotels of the company with the BI domain (BI_TOOL), also the hotels that the user cannot see.
	companyProperties: number,
	// The hotels of the account, in the order of the account, with their BI data.
	properties: BiProperty[],
};

// A Property group: a named set of properties of the company, for example "Rome". It belongs to the company: a company
// admin edits the groups, each BI user sees them. The server sends each group with only the properties that the user can
// read in BI, and leaves out a group with none of them. One property can be in more than one group.
export type PropertyGroup = {
	id: number,
	title: string,
	companyHotelIds: number[],
	// ROO2-314 item 7 (D36): a subtotal of Pick-up monitoring on All properties. No property is in two marked groups.
	subtotalOnAll: boolean,
};

// The answer of findPropertyGroups, savePropertyGroup and deletePropertyGroup: the groups in alphabetical order.
export type PropertyGroups = {
	groups: PropertyGroup[],
	// true: the user is a company admin and edits the groups.
	canEdit: boolean,
};

// One group to save: id null = a new group. The name has 1 to 40 characters and is unique in the company (case ignored).
export type PropertyGroupInput = {
	id: number | null,
	title: string,
	companyHotelIds: number[],
	// ROO2-314 item 7: the mark "Subtotal on All properties" (D36).
	subtotalOnAll: boolean,
};

// A Property group of the BI scope of the user, with its properties (in the order of the account).
export type BiPropertyGroup = {
	id: number,
	title: string,
	properties: BiProperty[],
};

// Situation D-n: what was on the books at the end of the day n days before today (n = 0 is today).
export const SITUATION_DAYS = [0, 1, 3, 7, 14, 30] as const;
export type SituationDays = typeof SITUATION_DAYS[number];

// The four revenues of a figure (ROO2-236). Only the Total with VAT always has a value.
export type RevenueValues = {
	// Room only, with VAT.
	roomRevenue: number | null,
	roomRevenueNet: number | null,
	// The room and the meals of the rate, with VAT.
	totalRevenue: number,
	totalRevenueNet: number | null,
};

export type SegmentSituation = RevenueValues & {
	daysBefore: SituationDays,
	roomNights: number,
	// ROO2-315 part c1: the occupied rooms of the hotels' report rules (= roomNights without rules); ADR divides by them.
	// Every report reads it since part c2; optional so that older fixtures still type (occupiedOf falls back to roomNights).
	occupiedRooms?: number,
};

// The revenue switches of the Segmentation page (ROO2-236): Room only (the default) or Total, Incl. VAT (the default) or Net.
export type RevenueScope = 'ROOM' | 'TOTAL';
export type VatBasis = 'INCL' | 'NET';
// ROO2-310 Options switch (D1–D8): EXCLUDE = a room on option is no room night (the default, the figures of before); INCLUDE =
// each figure counts the option nights from the day of the cut as sold. The page address keeps it (?options=include).
export type OptionsBasis = 'EXCLUDE' | 'INCLUDE';

// ROO2-283: the Columns choice of a BI report, kept in the user account (one list per user and report, in table order).
export type BiReport = 'SEGMENTATION' | 'ROOM_TYPES' | 'RATE_PLANS' | 'GUEST_MIX' | 'CHANNELS' | 'DAILY';
export type BiColumn = { id: string, visible: boolean };
export type BiColumns = { report: BiReport, columns: BiColumn[] };

export type SegmentationRowKind = 'GROUP' | 'SEGMENT' | 'NOT_GROUPED' | 'NO_SEGMENT';

export type SegmentationRow = {
	key: string,
	kind: SegmentationRowKind,
	title: string,
	situations: SegmentSituation[],
	// The PMS segments of a group.
	segments: SegmentationRow[],
};

export type SegmentationView = 'GROUPS' | 'SEGMENTS';

export type SegmentationReport = {
	view: SegmentationView,
	stayFrom: string,
	stayTo: string,
	rows: SegmentationRow[],
	total: SegmentationRow,
	// ROO2-315 part c1 (Q4): the report rules of the properties when they all have the same ones that change a figure (null
	// when none has any or they differ); rulesDiffer: the properties count by different rules.
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

export type EvolutionGrain = 'MONTH' | 'WEEK';

export type EvolutionPeriod = {
	key: string,
	label: string,
	from: string,
	to: string,
	// false: no imported stay in the period (before the first stay date).
	hasData: boolean,
};

export type EvolutionSeries = {
	key: string,
	kind: SegmentationRowKind,
	title: string,
	// One value per period; a revenue as in RevenueValues.
	roomNights: number[],
	// ROO2-315 part c1: the occupied rooms by the report rules (= roomNights without rules).
	occupiedRooms: number[],
	roomRevenue: (number | null)[],
	roomRevenueNet: (number | null)[],
	totalRevenue: number[],
	totalRevenueNet: (number | null)[],
};

// The periods of the stay months stayFrom–stayTo (one stay-month picker for the page): months, or weeks cut to the
// period (the first and the last week can be short, so the chart total equals the table total).
export type SegmentEvolution = {
	stayFrom: string,
	stayTo: string,
	grain: EvolutionGrain,
	view: SegmentationView,
	periods: EvolutionPeriod[],
	series: EvolutionSeries[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

export type SegmentGroup = {
	id: number,
	title: string,
	// PMS segment names. An assignment is keyed by the name (case and spaces ignored) for every property of the company.
	segments: string[],
};

export type PmsSegment = {
	name: string,
	// The properties of the company that use the name.
	properties: number,
	// Sold room nights over all imported stays.
	roomNights: number,
};


export type SegmentGroups = {
	groups: SegmentGroup[],
	segments: PmsSegment[],
	// true: the user may change the groups (a company admin).
	canEdit: boolean,
};

// The Excel file of the Segmentation report: the table as the page shows it (the View by, the Stay months, the revenue
// switches and the column sets of the Columns menu).
export type SegmentationExportInput = {
	// null = all properties of the BI scope.
	companyHotelId: number | null,
	// A Property group instead of one property (companyHotelId is then null).
	propertyGroupId: number | null,
	// The first and the last stay month, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	view: SegmentationView,
	scope: RevenueScope,
	vat: VatBasis,
	// The column sets of the Columns menu, in the order of the table: today, d1, d3, d7, d14, d30, pu1, pu3, pu7, pu14, pu30.
	columns: string[],
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// The stay months and the scope of a file of the Segment evolution card. "All data" (D15, D16) follows only these.
export type SegmentEvolutionDataInput = {
	// null = all properties of the BI scope.
	companyHotelId: number | null,
	// A Property group instead of one property (companyHotelId is then null).
	propertyGroupId: number | null,
	// The first and the last stay month, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// The metric tab of the Segment evolution card, as the export sends it.
export type EvolutionMetric = 'ROOM_NIGHTS' | 'REVENUE' | 'ADR' | 'MIX';

// "Current view" of the Segment evolution card (D5): every selection of the card travels (R1), the server has no default.
export type SegmentEvolutionExportInput = SegmentEvolutionDataInput & {
	grain: EvolutionGrain,
	view: SegmentationView,
	metric: EvolutionMetric,
	scope: RevenueScope,
	vat: VatBasis,
};

export type SegmentGroupInput = {
	// null for a new group.
	id: number | null,
	title: string,
	segments: string[],
};

// --- Room Types report (BI report 2, ROO2-262) ------------------------------------------------------------------------
// Each figure counts the nights under the room type of the basis: Booked = the room type that the guest booked
// (5stelle requested room type), Assigned = the room type of the room that the hotel gave in the PMS. The totals do not
// change with the basis. The rooms of a type (its inventory) do not change either.
export type RoomTypeBasis = 'BOOKED' | 'ASSIGNED';

// Sold room nights and their four revenues.
export type RoomTypeFigures = RevenueValues & {
	roomNights: number,
	// ROO2-315 part c1: the occupied rooms by the report rules (= roomNights without rules), for the ADR.
	occupiedRooms?: number,
};

export type RoomTypeMonth = RoomTypeFigures & {
	// YYYY-MM, a month of the period.
	month: string,
	// ROO2-272 D5: the sold room nights of the month on the Assigned basis of the same room type key, on both bases. Every
	// occupancy divides these by the rooms (a guest sleeps in the assigned room).
	assignedRoomNights: number,
	// ROO2-281 D3: the room-days of the type in the month: on each day, each physical room counts for the room type of its
	// latest booked night. Every occupancy divides by these; 0 = no value (n/a).
	roomDays: number,
	// ROO2-315 part c1 (Q3): assignedRoomNights by the report rules; the out-of-order rooms of the type (OPERA), at most
	// roomDays, when the rules take them off. Occupancy = assignedOccupiedRooms ÷ (roomDays − outOfOrderRooms).
	assignedOccupiedRooms: number,
	outOfOrderRooms: number,
};

// A booking channel (the PMS source of the booking) or a board behind a room type (RQ-25).
export type RoomTypeBreakdownRow = RoomTypeFigures & {
	name: string,
	// The bookings with a sold night of the type in the period, each once.
	reservations: number,
};

export type RoomTypeRowKind = 'ROOM_TYPE' | 'NO_ROOM_TYPE';

export type RoomTypeRow = {
	// The name in lower case without spaces: room types of two properties with the same key are one row (decision 7).
	key: string,
	kind: RoomTypeRowKind,
	// The spelling with the most sold nights.
	title: string,
	// The rooms of the type, from the Reservations integration; for a merged row the sum of the properties. null: no
	// inventory (at least one property of the row has no room count), so the occupancy has no value.
	rooms: number | null,
	// ROO2-281 D3: the room-days of the type in the period (the sum of its months); the denominator of every occupancy and of
	// the inventory share. `rooms` stays the label.
	roomDays: number,
	// The properties that hold the type.
	properties: number,
	// The bookings with a sold night of the type in the period, each once (decision 19).
	reservations: number,
	// Today and the situations D-1 … D-30 of the period: SITUATION_DAYS. PU n = today − situation D-n.
	situations: SegmentSituation[],
	// The same stay dates one year earlier, as they stood at the end of the as-of day one year earlier.
	stly: RoomTypeFigures,
	// The same stay dates one year earlier, as they stand today.
	lyActual: RoomTypeFigures,
	// Today's figures of the properties that have a sold night in the last-year period (decision 20): the base of
	// "vs STLY" and "vs LY actual", so that a property with no last-year data does not count as growth.
	comparable: RoomTypeFigures,
	// Today's figures per stay month of the period (the grid of the Analysis view).
	months: RoomTypeMonth[],
	// ROO2-272 D5: today's sold room nights on the Assigned basis of the same key (on both bases), the numerator of every
	// occupancy.
	assignedRoomNights: number,
	// ROO2-315 part c1 (Q3): as in RoomTypeMonth, for the period.
	assignedOccupiedRooms: number,
	outOfOrderRooms: number,
	channels: RoomTypeBreakdownRow[],
	boards: RoomTypeBreakdownRow[],
};

export type RoomTypeProperty = {
	companyHotelId: number,
	rows: RoomTypeRow[],
	total: RoomTypeRow,
};

export type RoomTypeReport = {
	basis: RoomTypeBasis,
	stayFrom: string,
	stayTo: string,
	// Merged by key over the properties of the scope, sorted by revenue (the Room only incl. VAT revenue of today).
	rows: RoomTypeRow[],
	// The sums of the scope; reservations = the bookings of the scope, each once.
	total: RoomTypeRow,
	// One block per property of the scope with sold nights (the By property table).
	properties: RoomTypeProperty[],
	// The properties of the scope with no sold night in the last-year period (decision 20).
	lastYearMissing: number[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// The Excel file of the Room Types report (RQ-32): the scope, the Stay months, the basis and the revenue switches of the
// page. The file holds every row and every column, whatever the Columns menu and the Rows | Transposed choice.
export type RoomTypesExportInput = {
	// null = all properties of the BI scope.
	companyHotelId: number | null,
	// A Property group instead of one property (companyHotelId is then null).
	propertyGroupId: number | null,
	// The first and the last stay month, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	basis: RoomTypeBasis,
	scope: RevenueScope,
	vat: VatBasis,
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// --- Rate Plans report (BI report 3, ROO2-263) ------------------------------------------------------------------------
// A Booked Rate Plan is the rate plan name that the PMS puts on a booked night (5stelle rate_name). The same name at two
// properties is one rate plan: the key is the name in lower case without spaces (decision 4); a night with no name is in
// "No rate plan" (key ''). The backend reads the Refund Policy and the Board of each night (RQ-06, RQ-07 and decision 3)
// and sends one part per rate plan, property, policy and board; the screen adds the parts up in the selected grouping.

// RQ-06: the first rule that matches the name decides.
export type RefundPolicy = 'NON_REFUNDABLE' | 'PROMOTIONS' | 'FLEXIBLE' | 'OTHER';
// RQ-07 and decision 3: the board word of the name; with no board word, the PMS board of the night (BB, HB, FB, AI and their
// combinations: MEALS; RO: ROOM_ONLY); with neither, NO_BOARD (decision 10).
export type RateBoard = 'MEALS' | 'ROOM_ONLY' | 'NO_BOARD';

// Sold room nights and their four revenues.
export type RatePlanFigures = RevenueValues & {
	roomNights: number,
	// ROO2-315 part c2: the occupied rooms by the report rules (= roomNights without rules), for the ADR.
	occupiedRooms?: number,
};

// One rate plan at one property with one policy and one board. A rate plan with no board word in its name can have two
// parts at a property: nights with the PMS board BB and nights with RO.
export type RatePlanPart = {
	// The name in lower case without spaces; '' = No rate plan.
	key: string,
	// The spelling of the name with the most sold nights in the scope.
	title: string,
	policy: RefundPolicy,
	board: RateBoard,
	companyHotelId: number,
	// Today and the situations D-1 … D-30 of the period: SITUATION_DAYS. PU n = today − situation D-n.
	situations: SegmentSituation[],
	// The same stay dates one year earlier, as they stood at the end of the as-of day one year earlier.
	stly: RatePlanFigures,
	// The same stay dates one year earlier, as they stand today.
	lyActual: RatePlanFigures,
};

export type RatePlanReport = {
	stayFrom: string,
	stayTo: string,
	// Every part with room nights today, at a situation D-n, at STLY or at LY actual (RQ-13).
	parts: RatePlanPart[],
	// The properties of the scope with no sold night in the last-year period: they stay out of Δ% (decision 16).
	lastYearMissing: number[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// The build-up of the period (ROO2-267, M-11), one point per day of the window, for each rate plan and board of the scope.
// Only the room nights and the revenue of the page's switches (revenueScope, vat): for All properties and 365 days, five
// figures per day would be more than 20 MB (T6). A series with 0 room nights on every day of both years is left out.
export type RatePlanEvolutionSeries = {
	key: string,
	title: string,
	policy: RefundPolicy,
	board: RateBoard,
	// One value per day of `days`: on the books at the end of that day.
	thisYearRn: number[],
	// null: a night on the books that day has no value for this revenue (never a partial sum, ROO2-236).
	thisYearRev: (number | null)[],
	// The same stay dates one year earlier, on the same day one year earlier ("Last year (same lead time)").
	lastYearRn: number[],
	lastYearRev: (number | null)[],
	// ROO2-315 part c2: the occupied rooms by the report rules (= the Rn lists without rules), for the ADR.
	thisYearOccupied?: number[],
	lastYearOccupied?: number[],
};

export type RatePlanEvolution = {
	// YYYY-MM-DD, the days of the window up to today (30, 90, 180 or 365 days).
	days: string[],
	series: RatePlanEvolutionSeries[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// The Excel file of the Rate Plans report (decisions 17–20, the export of ROO2-261): the table as the page shows it (Group
// by, Stay months, the revenue switches and the column sets of the Columns menu). BUILD-UP is not in the file.
export type RatePlanExportInput = {
	// null = all properties of the BI scope (the file holds the sum, no property rows).
	companyHotelId: number | null,
	// A Property group instead of one property (companyHotelId is then null).
	propertyGroupId: number | null,
	// The first and the last stay month, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	groupBy: 'POLICY_BOARD' | 'POLICY' | 'BOARD' | 'NONE',
	scope: RevenueScope,
	vat: VatBasis,
	// The column sets in the order of the table: today, ly, d1, d3, d7, d14, d30, pu1, pu3, pu7, pu14, pu30.
	columns: string[],
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// --- Guest Mix report (BI report 4, ROO2-264) -------------------------------------------------------------------------
// Who stays at the hotel: each booked room goes into one row of the split, with all its nights in the stay months. The
// backend gives each booked room its key (B3-B7) from all its nights in the newest version:
//   GUEST_TYPE      UNKNOWN (0 adults), FAMILY (one child or more), SOLO (1 adult), COUPLE (2), ADULTS_3_PLUS (3 or more)
//   NATIONALITY     the ISO-2 code of the guest; '' = Unknown
//   LENGTH_OF_STAY  N1, N2, N3_4, N5_7, N8_PLUS: all the nights of the booked room, also outside the stay months
//   LEAD_TIME       SAME_DAY, D1_3, D4_7, D8_30, M1_3 (31–90 days), M3_PLUS, UNKNOWN (no book day, or a book day after
//                   the check-in day): the Booking Lead Time, from the book day to the check-in day, company time zone
export type GuestMixSplit = 'GUEST_TYPE' | 'NATIONALITY' | 'LENGTH_OF_STAY' | 'LEAD_TIME';

// Sold room nights and their four revenues.
export type GuestMixFigures = RevenueValues & {
	roomNights: number,
	// ROO2-315 part c2: the occupied rooms by the report rules (= roomNights without rules), for the ADR.
	occupiedRooms?: number,
};

// One key of the split at one property.
export type GuestMixPart = {
	key: string,
	companyHotelId: number,
	// Today and the situations D-1 … D-30 of the period: SITUATION_DAYS. PU n = today − situation D-n.
	situations: SegmentSituation[],
	// The same stay dates one year earlier, as they stood at the end of the as-of day one year earlier.
	stly: GuestMixFigures,
	// The same stay dates one year earlier, as they stand today.
	lyActual: GuestMixFigures,
};

export type GuestMixReport = {
	split: GuestMixSplit,
	stayFrom: string,
	stayTo: string,
	// Every part with room nights today, at a situation D-n, at STLY or at LY actual.
	parts: GuestMixPart[],
	// The properties of the scope with no sold night in the last-year period: they stay out of Δ% and MIX Δ (like for like).
	lastYearMissing: number[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// ROO2-265: one point per day of the window for each key of the split.
export type GuestMixEvolutionSeries = {
	key: string,
	// One value per day of `days`: as it stood at the end of that day (no nights after the last night of the stay months).
	thisYear: GuestMixFigures[],
	// The same stay dates one year earlier, at the same day one year earlier ("Last year (same lead time)").
	lastYear: GuestMixFigures[],
};

export type GuestMixEvolution = {
	split: GuestMixSplit,
	// YYYY-MM-DD, the days of the window up to today (30, 90, 180 or 365 days).
	days: string[],
	series: GuestMixEvolutionSeries[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// The Excel file of the Guest Mix report (decision 8, the export of ROO2-261): the table as the page shows it (the split,
// Stay months, the revenue switches and the column sets of the Columns menu).
export type GuestMixExportInput = {
	// null = all properties of the BI scope (the file holds the sum, no property rows).
	companyHotelId: number | null,
	// A Property group instead of one property (companyHotelId is then null).
	propertyGroupId: number | null,
	// The first and the last stay month, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	split: GuestMixSplit,
	scope: RevenueScope,
	vat: VatBasis,
	// The column sets in the order of the table: today, ly, d1, d3, d7, d14, d30, pu1, pu3, pu7, pu14, pu30.
	columns: string[],
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// --- Channels report (BI report 5, ROO2-285) -----------------------------------------------------------------------------
// Which booking channels bring the business, against last year. The channel of a night is the channel of its booking: the
// source name that the PMS sends (5stelle reservations_sources_name). Grill D2: names that differ only in case or spaces
// are one channel (the key: the name in lower case without whitespace); no or a blank name = "No channel" (key '').
// Grill D1: the company makes its own Channel Groups (several keys in one row), as the Segment Groups.

// Sold room nights and their four revenues.
export type ChannelFigures = RevenueValues & {
	roomNights: number,
	// ROO2-315 part c2: the occupied rooms by the report rules (= roomNights without rules), for the ADR.
	occupiedRooms?: number,
};

// One raw channel name at one property.
export type ChannelPart = {
	// The name as the PMS sends it; '' = no or a blank name.
	name: string,
	// The channel key (D2); '' = No channel.
	key: string,
	companyHotelId: number,
	// Today and the situations D-1 … D-30 of the period: SITUATION_DAYS. PU n = today − situation D-n.
	situations: SegmentSituation[],
	// The same stay dates one year earlier, as they stood at the end of the as-of day one year earlier.
	stly: ChannelFigures,
	// The same stay dates one year earlier, as they stand today.
	lyActual: ChannelFigures,
};

export type ChannelReport = {
	stayFrom: string,
	stayTo: string,
	// Every part with room nights today, at a situation D-n, at STLY or at LY actual (RQ-08).
	parts: ChannelPart[],
	// The properties of the scope with no sold night in the last-year period: they stay out of Δ% (like-for-like).
	lastYearMissing: number[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// The build-up of the period (RQ-13, M-11): one point per day of the window, for each raw channel name of the scope. Only the
// room nights and the revenue of the page's switches (as the Rate Plans build-up, ROO2-267 T6).
export type ChannelEvolutionSeries = {
	name: string,
	key: string,
	thisYearRn: number[],
	// null: a night on the books that day has no value for this revenue.
	thisYearRev: (number | null)[],
	// The same stay dates one year earlier, on the same day one year earlier.
	lastYearRn: number[],
	lastYearRev: (number | null)[],
	// ROO2-315 part c2: the occupied rooms by the report rules (= the Rn lists without rules), for the ADR.
	thisYearOccupied?: number[],
	lastYearOccupied?: number[],
};

export type ChannelEvolution = {
	// YYYY-MM-DD, the days of the window up to today (30, 90, 180 or 365 days).
	days: string[],
	series: ChannelEvolutionSeries[],
	reportRules: IntegrationReportRules | null,
	rulesDiffer: boolean,
};

// A Channel Group of the company (D1): a named set of channel keys.
export type ChannelGroup = {
	id: number,
	title: string,
	keys: string[],
};

// A channel of the company in the groups editor (RQ-03, Q4, Q5): every key ever seen, with the bookings made in the last 12
// months (all stay dates, sold now) — 0 when it has none.
export type ChannelListItem = {
	key: string,
	// The spelling with the most room nights; every spelling of the key.
	title: string,
	spellings: string[],
	properties: number,
	roomNights: number,
	// Room only revenue incl. VAT.
	revenue: number,
	// The first booking day of the key in the company, YYYY-MM-DD. "New" (Q5) = after the last save, in no group.
	firstBooking: string | null,
};

export type ChannelGroups = {
	groups: ChannelGroup[],
	channels: ChannelListItem[],
	// The time of the last save of the groups, in the company time zone with its offset; null = never saved: no channel is "New" (Q5).
	savedAt: string | null,
	canEdit: boolean,
};

export type ChannelGroupInput = {
	id: number | null,
	title: string,
	keys: string[],
};

// The Excel files of the Channels page (ROO2-285 B8): every selection of the card travels, the server has no default.
export type ChannelExportInput = {
	companyHotelId: number | null,
	propertyGroupId: number | null,
	// YYYY-MM
	stayFrom: string,
	stayTo: string,
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
} & ({ file: 'ALL_DATA' } | {
	file: 'CURRENT_VIEW',
	// View by of the page: the Channel Groups of the company, or one row per channel key.
	view: 'GROUPS' | 'CHANNELS',
	scope: RevenueScope,
	vat: VatBasis,
	// The column id of the sort of the table and its direction; the mix follows the order of the table.
	sort: string,
	direction: 'ASC' | 'DESC',
} & ({ card: 'TABLE', columns: string[] } | { card: 'MIX', measure: 'REVENUE_SHARE' | 'RN_SHARE' | 'ADR', comparison: 'STLY' | 'LY_ACTUAL' }));
// --- Daily Performance report (BI report 6, ROO2-288) -------------------------------------------------------------------
// One property, one row per stay night. The answer of findDailyPerformance: the figures of each night of the window at each
// cut (D0, D1, D3, D7, D14, D30, STLY, LY_ACTUAL); a STLY or LY_ACTUAL row carries the night of this year that it stands for.
// A night with no row at a cut has 0.
export type StlyBasis = 'WEEKDAY' | 'DATE';
export type DailyCut = 'D0' | 'D1' | 'D3' | 'D7' | 'D14' | 'D30' | 'STLY' | 'LY_ACTUAL';

export type DailyNightCut = RevenueValues & {
	night: string,
	cut: DailyCut,
	roomNights: number,
	// ROO2-315 B6/B7: the occupied rooms of the night at the cut, by the report rules of the hotel (room nights + day-use rooms
	// + no-show rooms − house-use rooms, each when its rule says so). Equal to roomNights for a hotel with every default.
	// ROO2-309: with Room revenue "All room postings", the four revenues hold all room postings of the night at the cut.
	occupiedRooms: number,
	// ROO2-302 (D1): the held rooms of group blocks in roomNights at the cut: the rooms of deduct blocks that no guest booked yet
	// (OPERA counts them as sold), and with Options: Include from the as-of day those of non-deduct blocks. Their potential
	// revenue is in the four revenues, and they are occupied rooms. 0 for a hotel with no block.
	heldBlockRooms?: number,
};

// ROO2-315 B5/B7: the available rooms of a night: rooms − the out-of-order rooms of the night (OPERA's statistics) when the
// rule says so; every cut of a night uses the night's current count (part b Q3). One row per night of the window for a hotel
// with rules, none without. lastYearAvailableRooms: the same for the last-year night that the night stands for (STLY and
// Final, part b Q3). Null: no room count.
export type DailyAvailableNight = { night: string, outOfOrder: number, availableRooms: number | null, lastYearAvailableRooms: number | null };

// ROO2-314 Part 3: a row of findDailyNewNights: the room nights and revenues of the NEW bookings of one night in one PU block
// (the shared new-bookings rule, side B = situation D-n, side A = now); cut = the block (D1 … D30).
export type DailyNewNight = Omit<DailyNightCut, 'cut' | 'occupiedRooms'> & { cut: 'D1' | 'D3' | 'D7' | 'D14' | 'D30' };

export type DailyHeldNight = { night: string, blocked: number, onOption: number };

export type DailyPerformance = {
	companyHotelId: number,
	stayFrom: string,
	stayTo: string,
	stlyBasis: StlyBasis,
	// ROO2-310: the Options switch of the request, echoed. With Include the option nights are in nights[] from each cut day (D3,
	// D4), and held[] still gives the rooms on option of each night (Not for sale then counts them on past nights only, Q3).
	includeOptions: boolean,
	// The as-of day of the cuts (today in the company time zone) and the end of the newest reservations import of the hotel.
	asOf: string,
	importedAt: string | null,
	// The sum of the known room counts of the hotel's Reservations integration (D4); null: no count.
	rooms: number | null,
	// The active, not virtual room types with sold nights in the window and no room count (D36).
	roomTypesWithoutCount: string[],
	// D40: the first night with a counted room night in the hotel's data; a last-year night before it has no data.
	firstStayNight: string | null,
	// D38: per night, the rooms that the PMS holds but that are no room night: blocked and on option. Only the nights that have one.
	held: DailyHeldNight[],
	nights: DailyNightCut[],
	// ROO2-315 part b: the report rules of the hotel's Reservations integration (null: none, today's rule) and the available
	// rooms of the nights that differ from rooms.
	reportRules: IntegrationReportRules | null,
	available: DailyAvailableNight[],
};

// The Excel file of the Daily Performance report (B11). CURRENT_VIEW: the table as it shows (every selection travels);
// ALL_DATA: every column of every night and the month total.
export type DailyPerformanceExportInput = {
	companyHotelId: number,
	// The first and the last night of the window, YYYY-MM-DD.
	stayFrom: string,
	stayTo: string,
	stlyBasis: StlyBasis,
	file: 'CURRENT_VIEW' | 'ALL_DATA',
	scope: RevenueScope,
	vat: VatBasis,
	// The shown column ids in the user's order, night first.
	columns: string[],
	// MON … SUN and PAST.
	hide: string[],
	// "none" or "<column id>:asc|desc".
	sort: string,
	// The open months of a longer window, YYYY-MM.
	open: string[],
	// ROO2-310: the Options switch of the page (true = Include).
	includeOptions: boolean,
};

// ROO2-289: the compset of a night: the values of the RoomPulse day popup (DayValuesService, decisions G3, D17–D21).
export type DailyComparison = {
	price: number,
	// ceil((own ÷ price − 1) × 100); null without an own price.
	difference: number | null,
	tone: 'success' | 'warning' | 'critical' | 'none',
};

export type DailyCompsetDay = {
	date: string,
	status: 'OK' | 'NO_OWN_RATE' | 'NO_DATA',
	ownPrice: number | null,
	median: DailyComparison | null,
	weightedAverage: DailyComparison | null,
	updatedAt: string | null,
};

export type DailyCompset = {
	comparisonMethod: 'MEDIAN' | 'WEIGHTED_AVERAGE',
	weightedAverageAvailable: boolean,
	// The length of stay of the scan: the hotel's first default nights (B2).
	nights: number,
	// The currency of the prices (ISO code): the hotel's main currency.
	currency: string,
	days: DailyCompsetDay[],
};

// ROO2-298 (E8, Q2): the server reads the compset of a range in parts of 62 dates, one after the other, so a range of 12
// months takes about twice the time of 6 months. The page asks for a range of more than COMPSET_PART_NIGHTS nights (6
// whole months) in two halves at the same time, and joins the answers in night order.
const COMPSET_PART_NIGHTS = 184;
const DAY_MS = 86400000;
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const compsetParts = (from: string, to: string): { from: string, to: string }[] => {
	const start = Date.parse(`${from}T00:00:00Z`), nights = Math.round((Date.parse(`${to}T00:00:00Z`) - start) / DAY_MS) + 1;
	if (nights <= COMPSET_PART_NIGHTS) return [{ from, to }];
	const firstHalf = Math.floor(nights / 2);
	return [{ from, to: isoDay(start + (firstHalf - 1) * DAY_MS) }, { from: isoDay(start + firstHalf * DAY_MS), to }];
};
// The parts are of one property: the comparison method, the scan nights and the currency are the same in each.
export const joinCompset = (parts: DailyCompset[]): DailyCompset => ({ ...parts[0], days: parts.flatMap(p => p.days) });
// ROO2-301 U2, U4: runs task on each item in order, at most atOnce at the same time. Once stopped() is true no new task starts
// (a running one ends). A failed task does not stop the others.
export const runQueue = <T>(items: readonly T[], atOnce: number, task: (item: T) => Promise<unknown>, stopped: () => boolean): Promise<void> => {
	let next = 0;
	const worker = async (): Promise<void> => {
		while (next < items.length && !stopped()) {
			const item = items[next];
			next += 1;
			await task(item).catch(() => undefined);
		}
	};
	return Promise.all(Array.from({ length: Math.min(atOnce, items.length) }, worker)).then(() => undefined);
};

// --- Bookings behind a number (ROO2-284) -------------------------------------------------------------------------------
// A number of a BI table (RN or REV of today, of a situation D-n, of STLY, of LY actual, or a PU) opens the list of the
// bookings behind it (grill D9). The list uses the nights and the cuts of the report cell, so its total equals the cell.
export type BiBookingReport = 'CHANNELS';
// D0 … D30: the situation; STLY and LY_ACTUAL; PU n: the bookings whose nights or revenue changed between D-n and today.
export type BiBookingCut = 'D0' | 'D1' | 'D3' | 'D7' | 'D14' | 'D30' | 'STLY' | 'LY_ACTUAL' | 'PU1' | 'PU3' | 'PU7' | 'PU14' | 'PU30';
// The status of a booking of a situation (Q6): sold at the cut, and cancelled later (today) or not.
export type BiBookingStatus = 'SOLD' | 'CANCELLED_LATER';
// What changed in a booking of a PU list (Q4): + rows NEW, LONGER, MOVED_IN; − rows CANCELLED, SHORTER, MOVED_OUT; PRICE: the
// revenue changed and the room nights did not.
export type BiBookingChange = 'NEW' | 'LONGER' | 'SHORTER' | 'CANCELLED' | 'PRICE' | 'MOVED_IN' | 'MOVED_OUT';

// One booking (Q1: one row per reservation, with its rooms). No guest data (D9). In a PU list the room nights and the
// revenues are the change (today − D-n).
export type BiBookingRow = RevenueValues & {
	companyHotelId: number,
	reservationCode: string,
	// ROO2-314 D3: the reservation number (a 5stelle code without its booking time) and the OTA confirmation number.
	reservationNumber: string,
	otaNumber: string | null,
	// The book day (company time zone); null when the PMS sends none.
	bookDate: string | null,
	// The first stay night and the day after the last stay night of the counted nights in the period.
	arrival: string,
	departure: string,
	// The stay dates of the period that the booking holds, and its rooms.
	nights: number,
	rooms: number,
	roomNights: number,
	// The raw channel name of the version counted at the cut (Q5).
	channelName: string,
	status: BiBookingStatus | null,
	change: BiBookingChange | null,
};

export type BiBookings = {
	cut: BiBookingCut,
	stayFrom: string,
	stayTo: string,
	// More than 5,000 bookings (B5): no rows; the drawer asks for a smaller scope or period.
	more: boolean,
	rows: BiBookingRow[],
};

// --- Pick-up monitoring (ROO2-314 Part 2) ----------------------------------------------------------------------------------
// D24: the booked period of the page. The last N days end today; Yesterday is the day before today (company time zone).
export type PickupPeriod = 'TODAY' | 'YESTERDAY' | 'LAST3' | 'LAST7' | 'LAST14' | 'CUSTOM';
// The class of a booking between side B (the end of the day before the period) and side A (Part 1, D1, D26).
export type PickupChange = 'NEW' | 'CANCELLED' | 'LONGER' | 'SHORTER' | 'PRICE';
// The kind of a figure: new bookings, cancellations (positive), changes to older bookings (LONGER + SHORTER + PRICE) and
// net (NEW − CANCELLED + CHANGES).
export type PickupKind = 'NEW' | 'CANCELLED' | 'CHANGES' | 'NET';

// The room nights and the 4 revenues of one kind in one stay month (YYYY-MM) of one property. A revenue is null when a night
// of the cell has none (ROO2-236 R2).
export type PickupCell = {
	stayMonth: string,
	kind: PickupKind,
	roomNights: number,
	roomRevenue: number | null,
	roomRevenueNet: number | null,
	totalRevenue: number | null,
	totalRevenueNet: number | null,
};

export type PickupProperty = {
	companyHotelId: number,
	// The distinct bookings of each class (a class with none is left out).
	changes: { change: PickupChange, bookings: number }[],
	// The bookings booked and cancelled in the period: in no class (D25).
	madeAndCancelled: number,
	cells: PickupCell[],
};

// The answer of findPickupMonitoring: the properties of the scope with a Reservations provider, in the order of the scope.
export type PickupMonitoring = {
	bookFrom: string,
	bookTo: string,
	asOf: string,
	properties: PickupProperty[],
};

// ROO2-314 Part 2 (B2, B3): the four revenues of a booking change, of a booking now or of a reference; null = no value.
export type PickupRevenues = {
	roomRevenue: number | null,
	roomRevenueNet: number | null,
	totalRevenue: number | null,
	totalRevenueNet: number | null,
};

// One booking of the period (one row, all its rooms). roomNights / revenue: the change in the period (side A − side B; minus for
// a cancellation). The stay at side A (side B for a cancellation). bookedAt: the booking time in the company time zone
// (YYYY-MM-DDTHH:mm[:ss]). revenueNow and the references: on the books now (null when the booking has no night now).
export type PickupBooking = {
	id: string,
	reservationNumber: string,
	otaNumber: string | null,
	bookedAt: string | null,
	change: PickupChange,
	roomNights: number,
	revenue: PickupRevenues,
	rooms: number,
	nights: number,
	arrival: string | null,
	departure: string | null,
	channel: string,
	roomNightsNow: number,
	revenueNow: PickupRevenues | null,
	roomTypes: string[],
	refRoomType: PickupRevenues | null,
	refHotelNights: PickupRevenues | null,
	refArrivalMonth: PickupRevenues | null,
};

// The answer of findPickupBookings: tooMany = more than 5,000 bookings (then no booking, D13).
export type PickupBookings = {
	bookFrom: string,
	bookTo: string,
	asOf: string,
	tooMany: boolean,
	properties: { companyHotelId: number, bookings: PickupBooking[] }[],
};

// D9: the ADR alert setting of the user (one for all properties): the reference, the direction and the percent (1 to 100).
export type PickupAlertBasis = 'ROOM_TYPE' | 'HOTEL_NIGHTS' | 'HOTEL_MONTH' | 'BOOKED_DAY';
export type PickupAlertDirection = 'BELOW' | 'ABOVE' | 'BOTH';
export type PickupAlertSetting = { basis: PickupAlertBasis, direction: PickupAlertDirection, threshold: number };

// The "Bookings" files (D18): the scope, the period and the switches of the page; Current view or All data.
export type PickupBookingsExportInput = {
	companyHotelId: number | null,
	propertyGroupId: number | null,
	period: PickupPeriod,
	bookFrom: string | null,
	bookTo: string | null,
	file: 'CURRENT_VIEW' | 'ALL_DATA',
	scope: RevenueScope,
	vat: VatBasis,
	includeOptions: boolean,
};

// The "Pick-up by stay month" file (B5): the scope, the period and the switches of the page, the stay year and New | Net.
export type PickupExportInput = {
	companyHotelId: number | null,
	propertyGroupId: number | null,
	period: PickupPeriod,
	bookFrom: string | null,
	bookTo: string | null,
	year: string,
	show: 'NEW' | 'NET',
	scope: RevenueScope,
	vat: VatBasis,
	includeOptions: boolean,
};
